import React from "react";
import { Box, Text } from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import { useSalon } from "../../../context/SalonContext";
import { SalonContextType } from "../../../types/context";
import { getCheckoutReminder } from "../../../utils/checkoutReminder";
import styles from "./CheckoutReminderAlert.module.css";

export interface CheckoutReminderAlertProps {
  /** Eyebrow heading (rendered uppercase). */
  title: string;
  /** Reminder body; line breaks are preserved. */
  text: string;
  /** Emoji beside the title; null = the default info icon. */
  emoji: string | null;
}

/**
 * Presentational checkout reminder callout: emoji (or the default info icon) +
 * uppercase eyebrow title +
 * body on a soft burnt-orange fill. Used directly by the Settings preview; the
 * checkout surfaces use `ConnectedCheckoutReminderAlert`.
 */
export function CheckoutReminderAlert({
  title,
  text,
  emoji,
}: CheckoutReminderAlertProps) {
  return (
    <Box role="note" aria-label={title} className={styles.root}>
      <Box className={styles.icon} aria-hidden>
        {emoji ? (
          <Text component="span" className={styles.emoji}>
            {emoji}
          </Text>
        ) : (
          <IconInfoCircle size={18} data-testid="reminder-default-icon" />
        )}
      </Box>
      <Box className={styles.body}>
        <Text className={styles.title}>{title}</Text>
        <Text className={styles.text}>{text}</Text>
      </Box>
    </Box>
  );
}

/**
 * The salon's configured checkout reminder, read from SalonContext. Renders
 * nothing when the reminder is off or blank, so hosts can drop it in
 * unconditionally.
 */
export function ConnectedCheckoutReminderAlert() {
  const { businessInfo } = useSalon() as SalonContextType;
  const reminder = getCheckoutReminder(businessInfo);
  if (!reminder) return null;
  return <CheckoutReminderAlert {...reminder} />;
}

export default CheckoutReminderAlert;
